import { useStoreApi } from "reactflow"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import { useFormik } from "formik"
import type { FormikProps } from "formik"

import type { Script } from "../../../../types/generated/api/Script"
import type { ScriptGroupList } from "../../../../types/generated/api/ScriptGroupList"
import type { FeatureMapping, FeatureMappingFormikValues } from "../../types/interfaces"

interface UseScriptNodeFormProps {
  data?: {
    feature_mappings?: FeatureMapping[]
    script?: Script
    nodeType?: string
  }
  nodeId: string | null
  scripts?: ScriptGroupList[]
}

/**
 * useScriptNodeForm hook that manages form state for script node configuration.
 * Handles feature mapping, script selection, and node state updates.
 */
export const useScriptNodeForm = ({
  data,
  nodeId,
  scripts,
}: UseScriptNodeFormProps): FormikProps<FeatureMappingFormikValues> => {
  // React Flow hooks
  const store = useStoreApi()
  const { setNodes, getNodes } = store.getState()

  return useFormik<FeatureMappingFormikValues>({
    initialValues: {
      feature_mappings: data?.feature_mappings ?? [],
      retrievedNode: {
        uuid: data?.script?.uuid as string,
        feature_mappings: data?.feature_mappings as Array<FeatureMapping>,
      },
      liveModelSchema: [],
      cachedNodes: {},
    },

    onSubmit: async ({ feature_mappings }) => {
      // Update node state with feature mappings
      setNodes(
        getNodes().map((node) => {
          const newNode = { ...node }
          // check if current node is script node
          if (node.id === nodeId && scripts && data?.script) {
            // fetch the script selected in the current script node
            const script = scripts.find((script: ScriptGroupList) => script?.uuid === (data?.script as Script)?.uuid)
            // return all important data to react-flow state (feature_mappings, is_draft, isBuiltin)
            return {
              ...newNode.data,
              feature_mappings: feature_mappings,
              is_draft: script?.is_draft ?? false,
              isBuiltin: Boolean(script?.template),
            }
          }
          return newNode
        }),
      )

      NotificationUtils.toast(`${data?.nodeType?.replace("Node", " node")} removed!`, {
        severity: "success",
      })
    },
  })
}
